// composables/useNotificationHub.ts
// One SignalR connection per tab to the InternalApi's notification hub, shared by whoever needs it:
// the topbar follow while it is switched on, the Calls page while it is open. It connects with the
// first acquire and disconnects with the last release.
import { CALL_ANSWERED, CALLS_CHANGED, NOTIFICATION_HUB_PATH, REFUSED_WITHOUT_EMAIL } from '@/api/notifications';
import { acquireApiToken } from '@/auth/msal';
import { apiBaseUrl } from '@/composables/useApiClient';
import { HttpTransportType, HubConnectionBuilder, LogLevel, type HubConnection } from '@microsoft/signalr';
import { readonly, ref } from 'vue';

//disconnected: nobody needs it. connecting: starting or reconnecting. connected: pushes arrive.
//refused: the hub turned the account away for want of an e-mail claim, which retrying does not change
export type HubState = 'disconnected' | 'connecting' | 'connected' | 'refused';

const state = ref<HubState>('disconnected');
const holders = new Set<symbol>();
const handlers = new Map<string, Set<(...args: unknown[]) => void>>([
    [CALL_ANSWERED, new Set()],
    [CALLS_CHANGED, new Set()]
]);
const reconnectedHandlers = new Set<() => void>();
let connection: HubConnection | null = null;
let retry: ReturnType<typeof setTimeout> | undefined;
//set once a connection of this acquire cycle was up, so a later one counts as a reconnect
let wasConnected = false;
//how long to wait before starting again after a failed start or a lost connection
const RETRY_MS = 10_000;

function connected() {
    state.value = 'connected';
    if (wasConnected) reconnectedHandlers.forEach((handler) => handler());
    wasConnected = true;
}

async function connect() {
    clearTimeout(retry);
    const current = new HubConnectionBuilder()
        //WebSockets only: the hub reads the user from the request that opened the connection
        .withUrl(`${apiBaseUrl()}${NOTIFICATION_HUB_PATH}`, { accessTokenFactory: acquireApiToken, skipNegotiation: true, transport: HttpTransportType.WebSockets })
        //keeps trying while anyone holds it; the delay grows to half a minute
        .withAutomaticReconnect({ nextRetryDelayInMilliseconds: ({ previousRetryCount }) => Math.min(30_000, 1000 * 2 ** previousRetryCount) })
        .configureLogging(LogLevel.Warning)
        .build();
    connection = current;

    for (const [method, set] of handlers) current.on(method, (...args: unknown[]) => set.forEach((handler) => handler(...args)));
    current.onreconnecting(() => (state.value = 'connecting'));
    current.onreconnected(connected);

    //closed for good, or the start failed: a refusal stays refused; anything else starts again. The
    //refusal arrives right after the handshake, so it can end start() as well as an open connection
    const closed = (error?: Error) => {
        if (connection !== current) return;
        if (error?.message.includes(REFUSED_WITHOUT_EMAIL)) {
            state.value = 'refused';
            return;
        }
        state.value = 'connecting';
        retry = setTimeout(connect, RETRY_MS);
    };
    current.onclose(closed);

    state.value = 'connecting';
    try {
        await current.start();
        if (connection === current) connected();
    } catch (error) {
        closed(error instanceof Error ? error : undefined);
    }
}

async function disconnect() {
    clearTimeout(retry);
    const current = connection;
    connection = null;
    wasConnected = false;
    state.value = 'disconnected';
    await current?.stop();
}

export function useNotificationHub() {
    return {
        state: readonly(state),
        //returns the release; the connection stays while anyone holds it
        acquire() {
            const holder = Symbol();
            holders.add(holder);
            if (holders.size === 1) void connect();
            return () => {
                if (!holders.delete(holder)) return;
                if (holders.size === 0) void disconnect();
            };
        },
        //returns the unsubscribe
        on(method: string, handler: (...args: unknown[]) => void) {
            const set = handlers.get(method);
            if (!set) throw new Error(`No hub method ${method}`);
            set.add(handler);
            return () => void set.delete(handler);
        },
        //a reconnect may have missed pushes; returns the unsubscribe
        onReconnected(handler: () => void) {
            reconnectedHandlers.add(handler);
            return () => void reconnectedHandlers.delete(handler);
        }
    };
}
